import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { AxiosHeaderValue, AxiosPromise, AxiosResponse } from "axios";

import { resolvePhpApiEndpoint } from "../../features/axios-instances/php-api-endpoint";
import { PlatformMSAxiosInstance } from "../../features/axios-instances/platform";

function getURI({
  entity,
  verb = "list",
  params,
  endpoint,
}: {
  entity: string;
  verb?: string;
  params?: Record<string, string | number>;
  endpoint?: string;
}): string {
  return `${
    endpoint ?? resolvePhpApiEndpoint(import.meta.env.VITE_MS_API_ENDPOINT)
  }/webservices/rest/${verb}-${entity}${
    params
      ? `?${Object.entries(params)
          .map(
            ([name, value]) =>
              `${encodeURIComponent(name)}=${encodeURIComponent(
                Array.isArray(value) ? JSON.stringify(value) : value,
              )}`,
          )
          .join("&")}`
      : ""
  }`;
}

const runningRequests: Record<string, AxiosPromise> = {};

export async function axiosGet<T>({
  entity,
  verb,
  params,
  endpoint,
}: {
  entity: string;
  verb?: string;
  params?: Record<string, string | number>;
  endpoint?: string;
}): Promise<AxiosResponse<T>> {
  const url = getURI({ entity, verb, params, endpoint });
  const inFlight = runningRequests[url];
  if (inFlight) {
    return await inFlight;
  }
  const promise = PlatformMSAxiosInstance<T>(url, { method: "get" });
  runningRequests[url] = promise;
  try {
    return await promise;
  } finally {
    delete runningRequests[url];
  }
}

type getURIOptions = {
  entity: string;
  verb?: string;
  params?: Record<string, string | number>;
  token: string | null;
  body?: unknown;
  endpoint?: string;
  doNotRetry?: boolean;
};

export async function axiosPost<T = unknown, D = unknown>({
  entity,
  verb,
  params,
  token,
  body,
  endpoint,
  doNotRetry,
}: getURIOptions): Promise<AxiosResponse<T, D>> {
  const url = getURI({ entity, verb, params, endpoint });
  if (typeof runningRequests[url] !== "undefined") {
    return await runningRequests[url];
  }
  const headers = token ? { Authorization: token } : undefined;
  const promise = PlatformMSAxiosInstance<T>({
    method: "post",
    url,
    headers: headers,
    data: body,
    doNotRetry,
  });
  runningRequests[url] = promise;
  try {
    const result = await promise;
    delete runningRequests[url];
    return result;
  } catch (e) {
    delete runningRequests[url];
    throw e;
  }
}

export async function axiosDelete<T = unknown>({
  entity,
  verb,
  params,
  token,
  endpoint,
}: Omit<getURIOptions, "body">): Promise<AxiosResponse<T>> {
  const url = getURI({ entity, verb, params, endpoint });
  const headers = token ? { Authorization: token } : undefined;
  return PlatformMSAxiosInstance({
    method: "delete",
    url,
    headers,
  });
}

export async function axiosPatch<T = unknown, D = unknown>({
  entity,
  verb,
  params,
  token,
  body,
  endpoint,
}: getURIOptions): Promise<AxiosResponse<T, D>> {
  const url = getURI({ entity, verb, params, endpoint });
  if (typeof runningRequests[url] !== "undefined") {
    return await runningRequests[url];
  }
  const headers = token ? { Authorization: token } : undefined;
  const promise = PlatformMSAxiosInstance<T>({
    method: "patch",
    url,
    headers: headers,
    data: body,
  });
  runningRequests[url] = promise;
  try {
    const result = await promise;
    delete runningRequests[url];
    return result;
  } catch (e) {
    delete runningRequests[url];
    throw e;
  }
}

export function useAxiosGet<T>({
  entity,
  verb,
  params,
}: {
  entity: string;
  verb?: string;
  params?: Record<string, string | number>;
}): UseQueryResult<{
  body: T;
  headers: Record<string, AxiosHeaderValue | undefined>;
}> {
  return useQuery({
    queryKey: [entity, verb, params],
    async queryFn() {
      const res = await axiosGet<T>({
        entity,
        verb,
        params,
      });
      return {
        body: res.data,
        headers: res.headers,
      };
    },
  });
}
